/**
 * Clip Preloader Hook
 *
 * Downloads ALL video + audio clips as blobs on timeline open, keyed by sourcePath.
 * Provides blob URLs for instant playback without network fetch delays.
 *
 * Key design:
 * - Concurrency limit of 3 parallel downloads
 * - Keys cache by sourcePath (not clipId) so split/copied clips sharing same source reuse same blob
 * - AbortController per in-flight fetch, aborted on unmount or path removal
 * - URL.revokeObjectURL on cleanup
 *
 * Failure handling:
 * - Transient fetch errors retry with exponential backoff
 *   (``MAX_RETRY_ATTEMPTS`` attempts, base delay ``RETRY_BASE_MS``).
 * - A path is only marked permanently-errored AFTER retries exhaust.
 * - Permanently-errored paths surface via ``errorPaths`` so callers
 *   can render a tombstone instead of stalling on missing data.
 * - 0-byte responses are treated as soft failures and retried (a
 *   backend asset that's mid-write should heal on retry).
 *
 * Before this design the preloader added a path to ``errorPathsRef`` on
 * the FIRST failure, permanently — a single transient 5xx from the API
 * (e.g. during backend restart) would brick the timeline forever, even
 * after the user hard-refreshed.  See VideoEditorNode preview hang bug.
 */

import { useRef, useState, useEffect, useCallback } from 'react'
import { fetchMediaBlob } from '@ponpon/api-client'
import createDebug from 'debug'

const debug = createDebug('dw:timeline-playback:preload')

export interface UseClipPreloaderReturn {
  /** Get the blob URL for a given source path, or null if not yet loaded */
  getBlobUrl: (sourcePath: string) => string | null
  /** Whether all clips have been loaded */
  isReady: boolean
  /** Number of clips still loading */
  loadingCount: number
  /** Paths that failed to load after exhausting retries */
  errorPaths: string[]
}

interface BlobEntry {
  blobUrl: string
}

interface UseClipPreloaderProps {
  projectId: string | undefined
  sourcePaths: string[]
}

const MAX_CONCURRENCY = 3
const MAX_RETRY_ATTEMPTS = 3
const RETRY_BASE_MS = 500
const MAX_RETRY_DELAY_MS = 4_000

function _sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const id = window.setTimeout(resolve, ms)
    signal?.addEventListener(
      'abort',
      () => {
        window.clearTimeout(id)
        reject(new DOMException('aborted', 'AbortError'))
      },
      { once: true },
    )
  })
}

function _isRetryableError(err: Error): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') return false
  if (err instanceof Error) {
    const msg = err.message.toLowerCase()
    if (msg.includes('http 4')) {
      // Permanent client errors (404, 403, etc) — don't retry.
      // Exception: 408 / 429 are transient.
      if (msg.includes('http 408') || msg.includes('http 429')) return true
      return false
    }
    // Treat 5xx + network errors as retryable.
    return true
  }
  return true
}

/** Fetch a media blob with bounded retry on transient failures.
 *
 * Returns the successful Response, or throws on:
 *   * Abort (signal triggered or unmount cleanup)
 *   * Non-retryable status (4xx other than 408/429)
 *   * All retry attempts exhausted on a retryable error
 *
 * A 0-byte response body is treated as a soft failure (the asset may be
 * mid-write).  Caller should consume body and verify size.
 */
async function _fetchWithRetry(
  projectId: string,
  path: string,
  signal: AbortSignal,
): Promise<Response> {
  let lastErr: Error | undefined
  for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
    try {
      const response = await fetchMediaBlob(projectId, path, { signal })
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }
      return response
    } catch (err) {
      // Narrow once here so neither `lastErr` nor `_isRetryableError` has to
      // name `unknown`; a non-Error rejection is not retryable anyway.
      if (!(err instanceof Error)) throw err
      lastErr = err
      if (!_isRetryableError(err) || attempt === MAX_RETRY_ATTEMPTS) {
        throw err
      }
      const backoff = Math.min(
        RETRY_BASE_MS * 2 ** (attempt - 1),
        MAX_RETRY_DELAY_MS,
      )
      debug(
        'retry %d/%d for %s in %dms (last: %s)',
        attempt,
        MAX_RETRY_ATTEMPTS,
        path,
        backoff,
        err instanceof Error ? err.message : String(err),
      )
      await _sleep(backoff, signal)
    }
  }
  // Unreachable — loop either returns or throws.
  throw lastErr
}

export function useClipPreloader({
  projectId,
  sourcePaths,
}: UseClipPreloaderProps): UseClipPreloaderReturn {
  const blobMapRef = useRef<Map<string, BlobEntry>>(new Map())
  const inFlightRef = useRef<Map<string, AbortController>>(new Map())
  const errorPathsRef = useRef<Set<string>>(new Set())

  // Counter state to force re-renders when blobs load
  const [, setUpdateCounter] = useState(0)
  const forceUpdate = useCallback(() => setUpdateCounter((c) => c + 1), [])

  // Track loading count for isReady
  const [loadingCount, setLoadingCount] = useState(0)

  const getBlobUrl = useCallback((sourcePath: string): string | null => {
    return blobMapRef.current.get(sourcePath)?.blobUrl ?? null
  }, [])

  useEffect(() => {
    if (!projectId) return

    const currentPaths = new Set(sourcePaths)
    const blobMap = blobMapRef.current
    const inFlight = inFlightRef.current

    // Revoke blobs for paths no longer needed
    for (const [path, entry] of blobMap) {
      if (!currentPaths.has(path)) {
        debug('revoking blob for removed path: %s', path)
        URL.revokeObjectURL(entry.blobUrl)
        blobMap.delete(path)
      }
    }

    // Abort in-flight fetches for removed paths
    for (const [path, controller] of inFlight) {
      if (!currentPaths.has(path)) {
        debug('aborting fetch for removed path: %s', path)
        controller.abort()
        inFlight.delete(path)
      }
    }

    // Remove stale error paths
    for (const path of errorPathsRef.current) {
      if (!currentPaths.has(path)) {
        errorPathsRef.current.delete(path)
      }
    }

    // Determine which paths need downloading
    const needsDownload = sourcePaths.filter(
      (p) => !blobMap.has(p) && !inFlight.has(p) && !errorPathsRef.current.has(p)
    )

    if (needsDownload.length === 0) {
      setLoadingCount(inFlight.size)
      return
    }

    debug('starting preload for %d paths (concurrency=%d)', needsDownload.length, MAX_CONCURRENCY)

    // Semaphore-based concurrent download
    let queueIndex = 0
    let activeCount = 0
    let cancelled = false

    const processNext = () => {
      while (!cancelled && activeCount < MAX_CONCURRENCY && queueIndex < needsDownload.length) {
        const path = needsDownload[queueIndex++]
        // Skip if already loaded or cancelled by the time we get here
        if (blobMap.has(path) || cancelled) continue

        const controller = new AbortController()
        inFlight.set(path, controller)
        activeCount++
        setLoadingCount(inFlight.size)

        debug('fetching: %s', path)

        _fetchWithRetry(projectId, path, controller.signal)
          .then((response) => response.blob())
          .then((blob) => {
            if (cancelled) return
            if (blob.size === 0) {
              // Soft failure: a 0-byte response usually means an asset
              // mid-write or a broken upstream generation.  Mark as error
              // so the duration probe + player skip it rather than
              // serving an empty blob URL that the <video> element will
              // try and fail to decode.
              throw new Error(`empty response (0 bytes) for ${path}`)
            }
            const blobUrl = URL.createObjectURL(blob)
            blobMap.set(path, { blobUrl })
            inFlight.delete(path)
            activeCount--
            debug('loaded: %s (%d bytes)', path, blob.size)
            setLoadingCount(inFlight.size)
            forceUpdate()
            processNext()
          })
          .catch((err) => {
            if (cancelled || err.name === 'AbortError') return
            console.error(`Failed to preload clip after retries: ${path}`, err)
            errorPathsRef.current.add(path)
            inFlight.delete(path)
            activeCount--
            debug('error loading (exhausted retries): %s — %s', path, err.message)
            setLoadingCount(inFlight.size)
            forceUpdate()
            processNext()
          })
      }
    }

    processNext()

    return () => {
      cancelled = true
      // Note: we don't abort in-flight fetches here because the effect may
      // re-run due to sourcePaths identity change even when the actual paths
      // haven't changed. Aborts for stale paths are handled above.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, sourcePaths.join('|'), forceUpdate])

  // Cleanup all blob URLs on unmount
  useEffect(() => {
    const blobMap = blobMapRef.current
    const inFlight = inFlightRef.current

    return () => {
      for (const [, entry] of blobMap) {
        URL.revokeObjectURL(entry.blobUrl)
      }
      blobMap.clear()
      for (const [, controller] of inFlight) {
        controller.abort()
      }
      inFlight.clear()
    }
  }, [])

  const isReady = loadingCount === 0 && sourcePaths.length > 0 &&
    sourcePaths.every((p) => blobMapRef.current.has(p) || errorPathsRef.current.has(p))

  const errorPaths = Array.from(errorPathsRef.current)

  return { getBlobUrl, isReady, loadingCount, errorPaths }
}
